// Componente para mostrar plano, fotos y video de la propiedad con navegación

import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Componente para mostrar el plano, fotos y video de una propiedad
 * Orden de tabs: Plano > Fotos > Vídeo
 * Solo muestra tabs que tienen contenido configurado
 * @param {object} property - Objeto con información de la propiedad
 * @param {function} trackEvent - Función para tracking de eventos (opcional)
 */
export const PropertyMedia = ({ property, trackEvent }) => {
  const { t } = useTranslation();
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [floorplanError, setFloorplanError] = useState(false);
  const [photoError, setPhotoError] = useState(false);

  // Refs para tracking consolidado de fotos
  const photoSessionRef = useRef({
    startTime: null,
    photosViewed: new Set(),
    navigations: 0,
    maxPhotoIndex: 0,
  });
  const floorPlanStartTimeRef = useRef(null);

  // Detectar qué contenido multimedia está disponible
  const photos = property?.photoUrls || [];
  const hasFloorPlan = !!property?.floorPlanUrl;
  const hasPhotos = photos.length > 0;
  const hasVideo = !!property?.videoUrl;

  // Determinar la primera tab disponible (orden: plano > fotos > video)
  const getInitialMediaView = () => {
    if (hasFloorPlan) return 'floorplan';
    if (hasPhotos) return 'photos';
    if (hasVideo) return 'video';
    return null;
  };

  const [mediaView, setMediaView] = useState(getInitialMediaView());

  // ✅ TRACKING CONSOLIDADO: Enviar resumen de sesión de fotos
  const trackPhotoSessionSummary = () => {
    if (!trackEvent || !photoSessionRef.current.startTime) return;

    const session = photoSessionRef.current;
    const durationSeconds = Math.floor((Date.now() - session.startTime) / 1000);

    if (session.navigations > 0 || durationSeconds >= 2) {
      trackEvent('photo_session_summary', {
        totalPhotos: photos.length,
        photosViewed: session.photosViewed.size,
        navigations: session.navigations,
        maxPhotoReached: session.maxPhotoIndex + 1,
        durationSeconds,
        avgTimePerPhoto: session.photosViewed.size > 0
          ? Math.round(durationSeconds / session.photosViewed.size)
          : 0,
      });
    }

    photoSessionRef.current = {
      startTime: null,
      photosViewed: new Set(),
      navigations: 0,
      maxPhotoIndex: 0,
    };
  };

  // ✅ TRACKING: Registrar tiempo cuando sale del plano
  const trackFloorPlanViewDuration = () => {
    if (!trackEvent || !floorPlanStartTimeRef.current) return;

    const durationSeconds = Math.floor((Date.now() - floorPlanStartTimeRef.current) / 1000);

    if (durationSeconds >= 1) {
      trackEvent('floorplan_view_duration', { durationSeconds });
    }
  };

  // ✅ TRACKING: Cambio entre tabs de media
  const handleMediaViewChange = (view) => {
    if (mediaView === 'photos' && view !== 'photos') {
      trackPhotoSessionSummary();
    }

    if (mediaView === 'floorplan' && view !== 'floorplan') {
      trackFloorPlanViewDuration();
      floorPlanStartTimeRef.current = null;
    }

    if (view === 'photos' && mediaView !== 'photos') {
      photoSessionRef.current = {
        startTime: Date.now(),
        photosViewed: new Set([currentPhotoIndex]),
        navigations: 0,
        maxPhotoIndex: currentPhotoIndex,
      };

      if (trackEvent) {
        trackEvent('photo_view_start', { totalPhotos: photos.length });
      }
    }

    if (view === 'floorplan' && mediaView !== 'floorplan') {
      floorPlanStartTimeRef.current = Date.now();
      if (trackEvent) {
        trackEvent('floorplan_view_start', {});
      }
    }

    if (view === 'video' && trackEvent) {
      trackEvent('video_view_start', {});
    }

    setMediaView(view);
  };

  // ✅ TRACKING CONSOLIDADO: Navegación a foto siguiente
  const nextPhoto = () => {
    const newIndex = (currentPhotoIndex + 1) % photos.length;
    setCurrentPhotoIndex(newIndex);
    setPhotoError(false);

    if (photoSessionRef.current.startTime) {
      photoSessionRef.current.navigations += 1;
      photoSessionRef.current.photosViewed.add(newIndex);
      photoSessionRef.current.maxPhotoIndex = Math.max(
        photoSessionRef.current.maxPhotoIndex,
        newIndex
      );
    }
  };

  // ✅ TRACKING CONSOLIDADO: Navegación a foto anterior
  const prevPhoto = () => {
    const newIndex = (currentPhotoIndex - 1 + photos.length) % photos.length;
    setCurrentPhotoIndex(newIndex);
    setPhotoError(false);

    if (photoSessionRef.current.startTime) {
      photoSessionRef.current.navigations += 1;
      photoSessionRef.current.photosViewed.add(newIndex);
    }
  };

  // ✅ CLEANUP: Enviar resumen al desmontar componente
  useEffect(() => {
    return () => {
      if (mediaView === 'photos' && photoSessionRef.current.startTime) {
        trackPhotoSessionSummary();
      }
      if (mediaView === 'floorplan' && floorPlanStartTimeRef.current) {
        trackFloorPlanViewDuration();
      }
    };
  }, [mediaView]);

  return (
    <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl overflow-hidden border border-slate-700/50">
      {/* Tabs de selección - Orden: Plano, Fotos, Vídeo */}
      <div className="flex border-b border-slate-700">
        {hasFloorPlan && (
          <button
            onClick={() => handleMediaViewChange('floorplan')}
            className={`flex-1 px-4 py-3 font-medium transition-all ${
              mediaView === 'floorplan'
                ? 'bg-slate-700/50 text-white border-b-2 border-amber-500'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`}
          >
            {t('media.floorplan')}
          </button>
        )}
        {hasPhotos && (
          <button
            onClick={() => handleMediaViewChange('photos')}
            className={`flex-1 px-4 py-3 font-medium transition-all ${
              mediaView === 'photos'
                ? 'bg-slate-700/50 text-white border-b-2 border-amber-500'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`}
          >
            {t('media.photos', { count: photos.length })}
          </button>
        )}
        {hasVideo && (
          <button
            onClick={() => handleMediaViewChange('video')}
            className={`flex-1 px-4 py-3 font-medium transition-all ${
              mediaView === 'video'
                ? 'bg-slate-700/50 text-white border-b-2 border-amber-500'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`}
          >
            {t('media.video')}
          </button>
        )}
      </div>

      {/* Contenido del media */}
      <div className="aspect-video bg-slate-900 relative">
        {mediaView === 'floorplan' && hasFloorPlan && (
          floorplanError ? (
            <div className="w-full h-full flex items-center justify-center bg-slate-800">
              <span className="text-slate-400 text-sm">{t('media.floorplanUnavailable')}</span>
            </div>
          ) : (
            <img
              src={property.floorPlanUrl}
              alt={t('media.floorplanAlt')}
              className="w-full h-full object-contain"
              onError={() => setFloorplanError(true)}
            />
          )
        )}

        {mediaView === 'photos' && hasPhotos && (
          <div className="relative w-full h-full">
            {photoError ? (
              <div className="w-full h-full flex items-center justify-center bg-slate-800">
                <span className="text-slate-400 text-sm">{t('media.imageUnavailable')}</span>
              </div>
            ) : (
              <img
                src={photos[currentPhotoIndex]}
                alt={t('media.photoAlt', { index: currentPhotoIndex + 1 })}
                className="w-full h-full object-contain"
                onError={() => setPhotoError(true)}
              />
            )}

            {photos.length > 1 && (
              <>
                <button
                  onClick={prevPhoto}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
                  aria-label={t('media.prevPhoto')}
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={nextPhoto}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
                  aria-label={t('media.nextPhoto')}
                >
                  <ChevronRight className="w-6 h-6" />
                </button>

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                  {currentPhotoIndex + 1} / {photos.length}
                </div>
              </>
            )}
          </div>
        )}

        {mediaView === 'video' && hasVideo && (
          <iframe
            src={property.videoUrl}
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        )}
      </div>
    </div>
  );
};

export default PropertyMedia;
